// src/App.jsx — SubscriptionGuard granular por tipo de bloqueo
//
// CAMBIOS vs versión anterior:
//   ✅ /lesson/:lesson_id  → blockOnTokens={false}  (tokens solo bloquea speaking/writing)
//   ✅ /multigames         → blockOnTokens={false}  (minigames no usan tokens)
//   ✅ /glosario           → blockOnTokens={false}  (glosario no usa tokens)
//   ✅ /adventure          → blockOnTokens={false}  (IA Aventura usa sus propios cerebros)
//   ✅ /tutor              → sin cambios (bloquea en tokens — usa Spacy IA)
//   ✅ /spacy-tutor        → sin cambios (bloquea en tokens — usa Spacy IA)
//   ✅ overdue / paused    → todos siguen bloqueados igual que antes

import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import OnboardingPage from './pages/OnboardingPage.jsx';
// MICROSERVICIO ONBOARDING v3 - Motor de Diagnostico (pre-registro, anonimo).
// Convive con OnboardingPage (post-registro) sin tocarlo: son flujos distintos.
import DiagnosticoPage from './pages/DiagnosticoPage.jsx';
import { destinoDeLaSesion } from './sesion.js';
import RegisterPage from './pages/RegisterPage.jsx';
import BienvenidaPage from './pages/BienvenidaPage.jsx';
import ProfesoresPage from './pages/ProfesoresPage.jsx';
import MuroVitrina from './components/guards/MuroVitrina.jsx';
import LoginPage from './pages/LoginPage.jsx';
import EmailVerificationPage from './pages/EmailVerificationPage.jsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx';
import ResetPasswordPage from './pages/ResetPasswordPage.jsx';
import TutorPage from './pages/TutorPage.jsx';
import LessonPage from './pages/LessonPage.jsx';
import MultigamesPage from './pages/MultigamesPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
// EL GIMNASIO - hub de decision de entrenamiento, primera pantalla tras login.
import GimnasioPage from './pages/GimnasioPage.jsx';
// EVALUACION DE NIVEL - la radiografia del alumno por skills de Cambridge.
import EvaluacionPage from './pages/EvaluacionPage.jsx';
import PantallaConRed from './components/PantallaConRed.jsx';
import ModalRecarga from './components/recarga/ModalRecarga.jsx';
import ExamenNivelPage from './pages/ExamenNivelPage.jsx';
import DojoPage from './pages/DojoPage.jsx';
import ConsultorioPage from './consultorio/ConsultorioPage.jsx';
import EntrevistaPage from './pages/EntrevistaPage.jsx';
import AltoRendimientoPage from './pages/AltoRendimientoPage.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import AvisoCookies from './components/legal/AvisoCookies.jsx';
import Header from './components/Header.jsx';
import ProgressCard from './components/ProgressCard.jsx';
import LevelCard from './components/LevelCard.jsx';
import BuscadorClases from './components/BuscadorClases.jsx';
import LandingPage from './pages/LandingPage';
import BusinessLandingPage from './pages/BusinessLandingPage.jsx';
import AdminDashboard from './pages/AdminDashboard.jsx';
import GlossaryPage from './pages/GlossaryPage.jsx';
import PricingPage from './pages/PricingPage.jsx';
import ReactivarPage from './pages/ReactivarPage.jsx';
import CoachPage from './pages/CoachPage.jsx';


import NSLDashboard from './components/admin/command_center/NSLDashboard.jsx';
import SuperAdminRoute from './components/admin/command_center/SuperAdminRoute.jsx';
import TeacherRoute from './components/guards/TeacherRoute.jsx';
import PortalProfesorPage from './pages/PortalProfesorPage.jsx';

import SubscriptionGuard from './components/guards/SubscriptionGuard.jsx';

import { syncDeviceInfo } from "./utils/deviceInfo";
import usePageTracker from "./hooks/usePageTracker.js";

import './App.css';

// PORTAL DE EMPRESAS (EMPRESAS.md Fase 6): carga aparte. Lo abre un director
// de RR. HH. con el enlace de su empresa; no tiene por qué bajarse el gimnasio.
const EmpresaPortalPage = lazy(() => import('./pages/EmpresaPortalPage.jsx'));
const LegalPage = lazy(() => import('./pages/LegalPage.jsx'));

function HomePage() {
  usePageTracker("home", "Página de Inicio", null, "idle");
  const [levels, setLevels] = useState([]);
  const [progressSummary, setProgressSummary] = useState(null);
  /* Lo que el examen de onboarding detectó que le duele a ESTE alumno. Es lo
     que cierra la promesa: hizo el examen, le dijimos que se le rompen los
     comparativos, y ahora la lección que lo arregla viene señalada. */
  const [ruta, setRuta] = useState(null);
  /* La clase que el alumno acaba de encontrar con el buscador. Se apaga sola:
     es la respuesta a una pregunta que ya hizo, no un estado permanente. */
  const [halladaId, setHalladaId] = useState(null);
  /* Los exámenes publicados, agrupados por nivel: van al final de cada nivel del
     curso, como su meta. Se piden aparte; su fallo no puede tumbar el catálogo. */
  const [examsPorNivel, setExamsPorNivel] = useState({});

  const irALaClase = (r) => {
    setHalladaId(r.lesson_id);
    /* El scroll espera un momento a que el nivel se despliegue. Sin la espera,
       se hace scroll a una tarjeta que todavía está dentro de un acordeón
       cerrado y el alumno acaba mirando el sitio equivocado. */
    setTimeout(() => {
      document.getElementById(`leccion-${r.lesson_id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 260);
    /* Seis segundos: lo que se tarda en mirar la pantalla después del scroll.
       Más y se convierte en decorado; menos y se apaga antes de llegar. */
    setTimeout(() => setHalladaId(null), 6000);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      syncDeviceInfo(token);

      /* Va aparte del Promise.all de abajo a propósito: si el alumno todavía
         no se examinó, o esto falla, el catálogo tiene que cargar igual. Una
         guía que no está no puede dejar a nadie sin curso. */
      fetch("/api/ruta/mia", { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(setRuta)
        .catch(() => setRuta(null));

      /* Los exámenes publicados, para pintar su tarjeta al final del nivel que
         les toca. Aparte del catálogo: si esto falla, las lecciones cargan igual. */
      fetch("/api/v3/exams", { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          const mapa = {};
          (d?.exams || []).forEach(ex => {
            (mapa[ex.cefr_target] = mapa[ex.cefr_target] || []).push(ex);
          });
          setExamsPorNivel(mapa);
        })
        .catch(() => setExamsPorNivel({}));

      Promise.all([
        fetch("/api/levels", { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch("/api/progress/summary", { headers: { 'Authorization': `Bearer ${token}` } }),
      ])
        .then(async ([lvlRes, prgRes]) => {
          if (!lvlRes.ok || !prgRes.ok) throw new Error('Error de API');
          setLevels(await lvlRes.json());
          setProgressSummary(await prgRes.json());
        })
        .catch(console.error);
    }
  }, []);

  return (
    <div>
      <Header />
      <div className="dp-container">
        <main style={{ width: '100%' }}>
          <ProgressCard
            xp={progressSummary?.total_xp}
            levelTitle="Progreso Total"
            percentage={progressSummary?.overall_progress_percentage}
          />
          <BuscadorClases onEncontrada={irALaClase} />

          {/* La nomenclatura, dicha UNA vez. Antes cada tarjeta marcada llevaba
              su propia etiqueta ("COMPARATIVOS" encima de "Comparatives &
              Superlatives"), que es decir lo mismo dos veces y llenaba la
              pantalla. Explicado arriba, el contorno se entiende solo. */}
          {ruta?.tiene_examen && (ruta?.temas || []).length > 0 && (
            <p className="lc-leyenda">
              <span className="lc-leyenda-marca" />
              Contorno amarillo: lo que tu examen marcó
              <span className="lc-leyenda-sep">·</span>
              <span className="lc-leyenda-check">✓</span> clase terminada
              <span className="lc-leyenda-sep">·</span>
              % tu avance
            </p>
          )}

          {levels.map(level => (
            <LevelCard
              key={level.level_code}
              level_code={level.level_code}
              title={level.title}
              description={level.description}
              lessons={level.lessons}
              lessonsProgress={progressSummary?.lessons_progress}
              foco={ruta?.temas}
              halladaId={halladaId}
              exams={examsPorNivel[level.level_code]}
            />
          ))}
        </main>
      </div>
    </div>
  );
}

/**
 * Quien abre la direccion de NSL a secas.
 *
 * Sin sesion, el diagnostico —que es la carta de presentacion—. Con sesion,
 * donde le tocaria aterrizar si hubiera entrado por el login: el gimnasio, o su
 * agenda si es profesor. El destino sale de `sesion.js`, que es el MISMO
 * fichero que usa el login; si vivieran separados, el alumno acabaria en un
 * sitio distinto segun por donde entro.
 */
function PuertaDeEntrada() {
  const destino = destinoDeLaSesion();
  // `replace`: sin esto, el boton de atras del navegador devuelve a la raiz,
  // que vuelve a redirigir, y el alumno se queda atrapado sin poder retroceder.
  if (destino) return <Navigate to={destino} replace />;
  return <DiagnosticoPage />;
}

function App() {
  return (
    <>
      {/* El aviso de cookies (LEGAL.md, L3). Fuera de las rutas: es el mismo en
          todas las pantallas públicas y él decide cuándo aparece. */}
      <AvisoCookies />
      <Routes>

      {/* ── Rutas Públicas ─────────────────────────────────────────────── */}


      {/* PUERTA DE ENTRADA A NSL: la raiz es el Motor de Diagnostico.
          Decision de producto (20/08/2026) que sustituye el mapa de rutas de
          docs/onboarding-master-v2.md Parte III seccion 2, donde "/" era la
          landing. La landing informativa sigue existiendo intacta en /landing.

          ── PERO SOLO PARA QUIEN NO HA ENTRADO (26-09-2026) ─────────────────
          El diagnostico es para el VISITANTE. Con el dominio conectado, un
          alumno que escribe `non-stop-learning.com` caia aqui, y si ya lo habia
          hecho alguna vez veia el resumen con un boton que decia «Quiero mi
          plan» y lo mandaba a REGISTRARSE. Teniendo cuenta.

          No rompia nada, que es lo peor: funcionaba, no daba error, y le
          enseñaba la puerta de la calle a un cliente de pago todos los dias.

          Quien tiene sesion entra donde entraria por el login. Y quien QUIERA
          rehacer el diagnostico sigue teniendo `/diagnostico`, que no cambia. */}
      <Route path="/" element={<PuertaDeEntrada />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/business" element={<BusinessLandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* DONDE ATERRIZA QUIEN ACABA DE VERIFICAR SU CORREO.
          Sin muro y sin sesion, igual que la vitrina: aqui se llega desde un
          clic en un correo, en el navegador que sea —el del telefono, uno
          donde nunca hubo sesion—. Exigir token justo despues de verificar
          seria recibirle con una puerta cerrada. */}
      <Route path="/bienvenida" element={<BienvenidaPage />} />

      {/* LA VITRINA. Sin sesion y sin muro para quien NO tiene cuenta, a
          proposito: es la primera pantalla que ve alguien que llega por el
          enlace de un profesor, y pedirle cuenta antes de dejarle preguntar es
          perder a la mitad de la gente justo donde menos se puede permitir.
          Pero a quien SI tiene cuenta y se quedo sin acceso se le trata como en
          el resto del producto: `MuroVitrina` le lleva a la pared. Rob lo vio
          ya desplegado, 17-09-2026: «en las pestanas de Profesores no sale,
          deberia salir ahi». */}
      <Route path="/profesores" element={
        <MuroVitrina>
          <ProfesoresPage />
        </MuroVitrina>
      } />
      <Route path="/onboarding" element={<OnboardingPage />} />
      {/* Alias estable del diagnostico: se mantiene para los links directos
          de campanas de ads que ya apuntan aqui (Parte III seccion 2). */}
      <Route path="/diagnostico" element={<DiagnosticoPage />} />
      <Route path="/plans" element={<PricingPage />} />

      {/* La pared de reactivación va FUERA de <ProtectedRoute> a propósito: si
          estuviera dentro, la propia barrera redirigiría hacia ella una y otra
          vez. La página comprueba la sesión por su cuenta y manda al login si
          no hay ninguna. */}
      <Route path="/reactivar" element={<ReactivarPage />} />

      {/* LO LEGAL (LEGAL.md). Público y sin sesión: el aviso de privacidad se
          tiene que poder leer ANTES de crear una cuenta. */}
      <Route path="/privacidad" element={
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <LegalPage documento="privacidad" />
        </Suspense>
      } />
      <Route path="/terminos" element={
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <LegalPage documento="terminos" />
        </Suspense>
      } />
      <Route path="/cookies" element={
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <LegalPage documento="cookies" />
        </Suspense>
      } />
      {/* La declaración de edad (28-09-2026). Necesita página propia aunque sea
          corta: quien firma algo tiene que poder leerlo entero, y el aviso que
          se le pide a las cuentas antiguas enlaza aquí. */}
      <Route path="/edad" element={
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <LegalPage documento="edad" />
        </Suspense>
      } />

      {/* Portal de empresas: fuera de ProtectedRoute y de SubscriptionGuard.
          La empresa entra con enlace + contraseña, no con cuenta de persona. */}
      <Route path="/empresa/:clave" element={
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <EmpresaPortalPage />
        </Suspense>
      } />

      {/* ── Rutas de Alumno Autenticado ────────────────────────────────── */}
      <Route element={<ProtectedRoute />}>

        {/* EL GIMNASIO: hub de decision, primera pantalla tras el login.
            /home sigue intacto como el temario del curso, ahora es UNA de las
            estaciones del gimnasio y no la unica puerta de entrada. */}
        {/* Con muro: al vencerse la prueba de 2 días, o sin plan, el gimnasio
            no se juega. `blockOnTokens={false}` porque quedarse sin tokens no
            cierra lo que no usa IA. */}
        <Route path="/entrenar" element={
          <SubscriptionGuard blockOnTokens={false}>
            <GimnasioPage />
          </SubscriptionGuard>
        } />

        {/* EVALUACION DE NIVEL: presentarla es parte de cualquier plan (por eso
            NO lleva SubscriptionGuard); ver la radiografia si exige plan de
            pago, y ese muro vive en el backend, en GET /result. */}
        <Route path="/evaluacion" element={
          <SubscriptionGuard blockOnTokens={false}>
            {/* Con red: el 20-09-2026 un resultado guardado con la forma vieja
                dejó esta pantalla EN BLANCO, sin un solo aviso. */}
            <PantallaConRed seccion="evaluacion" nombre="tu radiografía de nivel">
              <EvaluacionPage />
            </PantallaConRed>
          </SubscriptionGuard>
        } />

        {/* EXAMENES DE NIVEL: el alumno presenta un examen concreto definido en
            el CRM. Sin SubscriptionGuard — presentarlo es parte de cualquier
            plan; el backend decide qué exámenes están publicados. */}
        <Route path="/examenes" element={
          <SubscriptionGuard blockOnTokens={false}>
            <PantallaConRed seccion="examenes" nombre="los exámenes de nivel">
              <ExamenNivelPage />
            </PantallaConRed>
          </SubscriptionGuard>
        } />

        {/* EL DOJO: formatos de alta intensidad. Sin SubscriptionGuard — tres
            de los cuatro formatos son 100% locales y no cuestan un token; el
            cuarto (Crisis Room) tiene su propio escudo en el backend. */}
        <Route path="/dojo" element={
          <SubscriptionGuard blockOnTokens={false}>
            <DojoPage />
          </SubscriptionGuard>
        } />

        {/* EL CONSULTORIO: el alumno trae una duda escrita con sus palabras y
            sale con tres pildoras y cinco ejercicios. Sin SubscriptionGuard en
            la ruta — el muro vive en el backend, donde `ActiveUser` protege las
            dos rutas que cuestan una llamada a la IA. */}
        <Route path="/consultorio" element={
          <SubscriptionGuard blockOnTokens={false}>
            <ConsultorioPage />
          </SubscriptionGuard>
        } />

        {/* INTERVIEW ROOM: microservicio aislado bajo /api/v3/interview. Sin
            SubscriptionGuard en la ruta — el muro vive en el backend, donde las
            llamadas de IA exigen plan activo via ActiveUser. */}
        <Route path="/entrevista" element={
          <SubscriptionGuard blockOnTokens={false}>
            <EntrevistaPage />
          </SubscriptionGuard>
        } />

        {/* ALTO RENDIMIENTO: los seis entrenamientos B2+. Cuatro son 100%
            locales; los otros dos tienen su escudo de IA en el backend. */}
        <Route path="/alto-rendimiento" element={
          <SubscriptionGuard blockOnTokens={false}>
            <AltoRendimientoPage />
          </SubscriptionGuard>
        } />

        {/* El temario del curso, con muro: es el producto. `/dashboard` sí se
            queda abierto, que es donde ve su plan y lo activa. */}
        <Route path="/home" element={
          <SubscriptionGuard blockOnTokens={false}>
            <HomePage />
          </SubscriptionGuard>
        } />
        {/* Spacy Coach. Dentro de ProtectedRoute: gasta dinero y necesita
            sesion, y ademas la barrera de la prueba vencida tiene que
            alcanzarle como a todo lo demas. */}
        {/* Con muro, y con permiso expreso de Rob (11-09-2026) para tocar una
            pantalla de Spacy: es la que más dinero gasta, y hasta hoy un alumno
            con la prueba vencida entraba y el corte le saltaba al escribir la
            primera pregunta. `blockOnTokens` en `true` porque gasta IA. */}
        <Route path="/coach" element={
          <SubscriptionGuard blockOnTokens={true}>
            <CoachPage />
          </SubscriptionGuard>
        } />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* ── LECCIÓN: bloquea overdue/paused — tokens solo en SpeakingExercise y WritingExercise ── */}
        <Route path="/lesson/:lesson_id" element={
          <SubscriptionGuard blockOnTokens={false}>
            <LessonPage />
          </SubscriptionGuard>
        } />

        {/* ── MINIGAMES: bloquea overdue/paused — NO bloquea en tokens ── */}
        <Route path="/multigames" element={
          <SubscriptionGuard blockOnTokens={false}>
            <MultigamesPage />
          </SubscriptionGuard>
        } />

        {/* ── TUTOR IA: bloquea también en tokens (usa Spacy IA directamente) ──
            El rótulo decía esto y el código hacía lo contrario: `blockOnTokens`
            venía en `false`, así que un alumno sin tokens entraba y se estrellaba
            contra el error de la primera pregunta. Rob, 11-09-2026: los que
            llevan IA se cierran con opción a comprar tokens. */}
        <Route path="/tutor" element={
          <SubscriptionGuard blockOnTokens={true}>
            <TutorPage />
          </SubscriptionGuard>
        } />

        {/* ── /spacy-tutor RETIRADA el 30-08-2026 ──────────────────────────
            Era una pagina huerfana: NADA enlazaba a ella —el menu lleva a
            `/tutor`, que es otra y funciona— y su camino principal llamaba a
            `/api/spacy/lessons`, una ruta que no existe. Quien entrara
            escribiendo la URL veia «Error al obtener las lecciones».

            Se retira en vez de crear la ruta que le falta: mantener una
            segunda pantalla de tutor que nadie usa es superficie que hay que
            cuidar para siempre. Esta en git si algun dia se quiere.
        */}

        {/* ── GLOSARIO: bloquea overdue/paused — NO bloquea en tokens ── */}
        <Route path="/glosario" element={
          <SubscriptionGuard blockOnTokens={false}>
            <GlossaryPage />
          </SubscriptionGuard>
        } />



        {/* Admin Antiguo — sin restricción de suscripción */}
        <Route path="/admin/dashboard" element={<AdminDashboard />} />

      </Route>

      {/* ── Zona Super Admin ──────────────────────────────────────────── */}
      {/* ── El portal del profesor ─────────────────────────────────────
          Fuera de `ProtectedRoute` a proposito: ese guardia manda a
          `/onboarding` a quien no tenga `learning_profile`, y un profesor
          creado desde el CRM no tiene ninguno — nunca hizo el diagnostico.
          Pasarlo por ahi lo dejaria dando vueltas en el onboarding de un
          alumno sin llegar nunca a su agenda.

          El portal se SUMA a la plataforma normal: el profesor sigue
          entrando al gimnasio como cualquiera. Esto es una puerta mas. */}
      <Route element={<TeacherRoute />}>
        <Route path="/profesor" element={<PortalProfesorPage />} />
      </Route>

      <Route element={<SuperAdminRoute />}>
        <Route path="/command-center" element={<NSLDashboard />} />
      </Route>

      </Routes>

      {/* La recarga de energía vive aquí, fuera de las rutas: se abre encima de
          la pantalla en la que esté el alumno —su panel, una clase, el chat—
          porque quedarse sin energía pasa siempre en mitad de otra cosa
          (20-09-2026). Escucha el evento `nsl:recargar`. */}
      <ModalRecarga />
    </>
  );
}

export default App;